import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import { filter, isEmpty, isNil, size, trim } from 'lodash';

import {
  AssociationStatusFilter,
  AssociationStatusFilterLabel,
  SigLengthLimits,
} from 'enums/SigManagement.enum';

import { toDirectionIdentity } from '../utils/directionIdentity';
import AssociationCard from './AssociationCard';
import AssociationStatusChip from './AssociationStatusChip';
import { ACTION_GROUP_SX, ROW_SX, TEXT_SX } from './associationRowStyles';

const statusFilterOptions = [
  { value: AssociationStatusFilter.ALL, label: AssociationStatusFilterLabel.ALL },
  { value: AssociationStatusFilter.ACTIVE, label: AssociationStatusFilterLabel.ACTIVE },
  { value: AssociationStatusFilter.INACTIVE, label: AssociationStatusFilterLabel.INACTIVE },
];

const applyStatusFilter = (directions, statusFilter) => {
  if (statusFilter === AssociationStatusFilter.ALL) {
    return directions;
  }

  return filter(
    directions,
    ({ isActive }) => isActive === (statusFilter === AssociationStatusFilter.ACTIVE)
  );
};

/**
 * The directions this sig resolves, and the staged changes to them.
 *
 * Every action here only stages. Nothing reaches the server until Commit changes, which is why the
 * rows render from the loaded list with the draft applied on top rather than from the draft alone.
 *
 * There is one action per row, not two. The row is never deleted — setting it inactive keeps the
 * record and flips its status, so "remove" and "make inactive" would have been the same write under
 * two names.
 */
const SigDirectionAssociationsPanel = ({
  directions,
  directionCount,
  newDirections,
  loading,
  isCreating,
  onStageStatus,
  onStageNew,
  onUnstageNew,
}) => {
  const [statusFilter, setStatusFilter] = useState(AssociationStatusFilter.ALL);
  const [directionText, setDirectionText] = useState('');
  const visibleDirections = applyStatusFilter(directions, statusFilter);
  const trimmedDirectionText = trim(directionText);
  // Both ends. This had a minimum only, so a direction past its varchar(510) column reached the
  // server and came back as a driver error.
  const tooShort = trimmedDirectionText.length < SigLengthLimits.DIRECTION_MIN;
  const tooLong = trimmedDirectionText.length > SigLengthLimits.DIRECTION_MAX;

  /**
   * The direction already on this sig that the typed text would be, if there is one.
   *
   * The catalog card beside this one has always refused a duplicate — its picker disables an item
   * the sig already holds. This card takes free text, so it could not disable anything and did not
   * check at all: a direction the sig already had was staged, listed under Pending changes,
   * committed, merged into nothing by the server and reported as a success. The reviewer was told
   * the change had been applied when nothing had happened (POM-6142, reported on QA).
   *
   * Matched on the server's own rule for direction identity rather than on the raw string, so
   * "Take  1 Tablet" is caught against a stored "Take 1 tablet" — the server would have treated
   * them as one direction, so the screen has to as well.
   */
  const typedIdentity = toDirectionIdentity(trimmedDirectionText);
  const existingDirection = directions.find(
    ({ direction }) => toDirectionIdentity(direction) === typedIdentity
  );
  const isAlreadyStaged = newDirections.some(
    ({ direction }) => toDirectionIdentity(direction) === typedIdentity
  );
  const isDuplicate = !isEmpty(typedIdentity) && (!isNil(existingDirection) || isAlreadyStaged);

  const canAddDirection = !tooShort && !tooLong && !isDuplicate;

  const addHelperText = () => {
    if (tooLong) {
      return `A direction cannot be longer than ${SigLengthLimits.DIRECTION_MAX} characters`;
    }

    // Named apart from the plain duplicate, because the way out is different and not obvious: the
    // row is on the sig already, greyed out, and what the reviewer wants is the Set active button
    // beside it rather than a second copy.
    if (existingDirection && !existingDirection.isActive) {
      return 'This direction is already on this sig but is inactive. Set it active in the list above instead.';
    }

    if (existingDirection) {
      return 'This direction is already on this sig';
    }

    if (isAlreadyStaged) {
      return 'This direction is already staged to be added';
    }

    return undefined;
  };

  const handleAddDirection = () => {
    onStageNew(trimmedDirectionText);
    setDirectionText('');
  };

  const nothingToShow = !loading && size(visibleDirections) === 0 && size(newDirections) === 0;

  return (
    <AssociationCard
      // While creating, the count is the staged list — there is no server-side list for it to be
      // about, and a heading reading "Directions (0)" above three of them contradicts the card.
      title={`Directions (${isCreating ? size(newDirections) : directionCount})`}
      // One select rather than three toggle buttons: it takes a third of the width, and beside the
      // other card's heading the buttons read as that card's filter. Dropped while creating: every
      // row is a staged addition, so there is nothing a status could filter to.
      headerAction={
        isCreating ? undefined : (
          <TextField
            select
            size='small'
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            label='Status'
            sx={{ minWidth: 132 }}
          >
            {statusFilterOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        )
      }
      footer={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size='small'
            placeholder='Add a direction'
            value={directionText}
            onChange={(event) => setDirectionText(event.target.value)}
            error={tooLong || isDuplicate}
            helperText={addHelperText()}
            // No maxLength: it truncates a paste silently, and a direction quietly cut short is
            // worse than one refused out loud. The length is refused and explained instead.
            inputProps={{ 'aria-label': 'Add a direction' }}
          />
          <Button
            variant='contained'
            disableElevation
            startIcon={<AddIcon />}
            disabled={!canAddDirection}
            onClick={handleAddDirection}
            sx={{ flexShrink: 0 }}
          >
            Add
          </Button>
        </Box>
      }
    >
      {loading && <Skeleton variant='rounded' height={120} />}

      {nothingToShow && (
        <Typography variant='body2' color='text.secondary'>
          {/* eslint-disable-next-line no-nested-ternary */}
          {isCreating
            ? 'Add at least one direction — a sig with none matches nothing'
            : size(directions) === 0
              ? 'No directions linked to this sig yet'
              : 'No directions match this filter'}
        </Typography>
      )}

      {!loading && (
        <List dense disablePadding>
          {/* Staged additions first: they are the thing just done, and they do not exist
              server-side yet so they have no place in the sorted list below. */}
          {newDirections.map(({ draftId, direction }, index) => (
            <React.Fragment key={draftId}>
              {index > 0 && <Divider component='li' />}
              <ListItem disableGutters sx={ROW_SX}>
                <ListItemText sx={TEXT_SX} primary={direction} />
                <Box sx={ACTION_GROUP_SX}>
                  <Chip size='small' color='info' variant='outlined' label='Adding' />
                  <Tooltip title='Remove this addition'>
                    <IconButton
                      aria-label={`Remove added direction ${direction}`}
                      onClick={() => onUnstageNew(draftId)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}

          {visibleDirections.map(
            ({ directionId, direction, isActive, isStaged, serverIsActive }, index) => (
              <React.Fragment key={directionId ?? direction}>
                {(index > 0 || size(newDirections) > 0) && <Divider component='li' />}
                <ListItem disableGutters sx={ROW_SX}>
                  <ListItemText sx={TEXT_SX} primary={direction} />
                  <Box sx={ACTION_GROUP_SX}>
                    <AssociationStatusChip
                      isActive={isActive}
                      serverIsActive={serverIsActive}
                      isStaged={isStaged}
                    />
                    <Tooltip title={isActive ? 'Set inactive' : 'Set active'}>
                      <IconButton
                        aria-label={`${isActive ? 'Set inactive' : 'Set active'}: ${direction}`}
                        onClick={() => onStageStatus({ directionId, isActive: !isActive })}
                      >
                        {isActive ? <DoDisturbOnOutlinedIcon /> : <CheckCircleOutlineIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              </React.Fragment>
            )
          )}
        </List>
      )}
    </AssociationCard>
  );
};

export default SigDirectionAssociationsPanel;

SigDirectionAssociationsPanel.propTypes = {
  directions: PropTypes.arrayOf(
    PropTypes.shape({
      directionId: PropTypes.string,
      direction: PropTypes.string,
      isActive: PropTypes.bool,
      isStaged: PropTypes.bool,
      serverIsActive: PropTypes.bool,
    })
  ).isRequired,
  directionCount: PropTypes.number,
  newDirections: PropTypes.arrayOf(
    PropTypes.shape({ draftId: PropTypes.string, direction: PropTypes.string })
  ),
  loading: PropTypes.bool,
  isCreating: PropTypes.bool,
  onStageStatus: PropTypes.func.isRequired,
  onStageNew: PropTypes.func.isRequired,
  onUnstageNew: PropTypes.func.isRequired,
};

SigDirectionAssociationsPanel.defaultProps = {
  directionCount: 0,
  newDirections: [],
  loading: false,
  isCreating: false,
};
